import React, { useState, useEffect } from 'react';
import { useFeatureLimits } from './FeatureLimitsProvider';
import { FEATURE_NAMES, FEATURE_DISPLAY_NAMES } from '../../apis/featureLimits';
import { Card, ProgressBar, Alert, Button, Row, Col, Badge } from 'react-bootstrap';
import { FaCrown, FaGift, FaUsers, FaChartLine } from 'react-icons/fa';
import SideNav from '../helpers/SideNavbar';
import { useNavigate } from 'react-router-dom';

const FeatureLimitsDashboard = () => {
  const navigate = useNavigate();
  const {
    userLimits,
    subscriptionStatus,
    loading,
    error,
    loadUserData,
    isFreeUser,
    isSubscriber,
    getSubscriptionDisplayName,
    getRemainingLimit,
    hasReachedLimit,
    getUsagePercentage
  } = useFeatureLimits();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  if (loading) {
    return (
      <SideNav>
        <div className="d-flex justify-content-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </SideNav>
    );
  }

  if (error) {
    return (
      <SideNav>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Limits</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={loadUserData}>
            Retry
          </Button>
        </Alert>
      </SideNav>
    );
  }

  const getLimitColor = (featureName) => {
    const percentage = getUsagePercentage(featureName);
    if (percentage >= 90) return 'danger';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  const getLimitIcon = (featureName) => {
    const icons = {
      [FEATURE_NAMES.KNACK_MOMENTS]: '🎯',
      [FEATURE_NAMES.KNACK_FEEDS]: '📰',
      [FEATURE_NAMES.MANUAL_POSTS]: '✍️',
      [FEATURE_NAMES.POLLS]: '📊',
      [FEATURE_NAMES.SCHEDULE_DAYS]: '📅',
      [FEATURE_NAMES.CHALLENGES]: '🏆'
    };
    return icons[featureName] || '📝';
  };

  const isFeatureBlocked = (featureName) => {
    const limit = userLimits.find(l => l.feature_name === featureName);
    return limit && limit.limit_value === 0;
  };

  const getFeatureStatus = (featureName) => {
    if (isFeatureBlocked(featureName)) {
      return { status: 'blocked', message: 'Not available for free users' };
    }
    
    if (hasReachedLimit(featureName)) {
      return { status: 'limit-reached', message: 'Limit reached' };
    }
    
    return { status: 'available', message: 'Available' };
  };

  return (
    <SideNav>
      <div className="container-fluid p-2">
      <div className="row mb-2">
        <div className="col-12">
          <Card className="shadow-sm">
            <Card.Header className="text-white" style={{ backgroundColor: '#2e3192' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">
                    <FaChartLine className="me-2" />
                    Feature Usage Dashboard
                  </h4>
                  <small>Track your feature usage and limits</small>
                </div>
                <div className="text-end">
                  <div className="d-flex flex-column align-items-end" style={{ minWidth: '140px' }}>
                    {isFreeUser() ? (
                      <>
                        <span className="badge bg-warning px-3 py-1 mb-2" style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          color: '#000',
                          borderRadius: '20px',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          Free Trial
                        </span>
                        <small className="text-white mb-3" style={{ 
                          fontSize: '12px', 
                          opacity: 0.95,
                          fontWeight: '500',
                          textAlign: 'right',
                          width: '100%'
                        }}>
                          Limited features
                        </small>
                        <Button 
                          variant="light" 
                          size="sm"
                          onClick={() => navigate('/subscription')}
                          className="d-flex align-items-center justify-content-center"
                          style={{ 
                            backgroundColor: '#ffffff',
                            color: '#2e3192',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 16px',
                            fontWeight: '600',
                            fontSize: '13px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            transition: 'all 0.3s ease',
                            minWidth: '120px',
                            width: '100%'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                          }}
                        >
                          <FaCrown className="me-2" style={{ color: '#ffc107' }} />
                          Upgrade
                        </Button>
                      </>
                    ) : (
                      <div className="d-flex flex-column align-items-end" style={{ minWidth: '140px' }}>
                        <span className="badge bg-white px-3 py-1 mb-2" style={{ 
                          fontSize: '11px', 
                          fontWeight: '700', 
                          color: '#2e3192', 
                          borderRadius: '20px',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          ✨ Premium
                        </span>
                        <small className="text-white-50" style={{ fontSize: '12px', fontWeight: '500' }}>
                          Billed {getSubscriptionDisplayName().toLowerCase()}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row">
        {Object.values(FEATURE_NAMES).map(featureName => {
          const limit = userLimits.find(l => l.feature_name === featureName);
          const status = getFeatureStatus(featureName);
          const percentage = getUsagePercentage(featureName);
          const remaining = getRemainingLimit(featureName);

          return (
            <div key={featureName} className="col-md-6 col-lg-4 mb-2">
              <Card className={`h-100 ${status.status === 'blocked' ? 'border-danger' : ''}`}>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="fs-4 me-2">{getLimitIcon(featureName)}</span>
                    <strong>{FEATURE_DISPLAY_NAMES[featureName]}</strong>
                  </div>
                  <Badge bg={status.status === 'blocked' ? 'secondary':getLimitColor(featureName)}>
                    {status.status === 'blocked' ? 'Blocked' : 
                     status.status === 'limit-reached' ? 'Limit Reached' : 'Available'}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  {status.status === 'blocked' ? (
                    <Alert variant="secondary"  className="mb-0">
                      <strong>Not Available</strong><br />
                      <small>This feature is not available for free users. Upgrade to access.</small>
                    </Alert>
                  ) : (
                    <>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Usage</span>
                          <span>{limit ? limit.current_usage : 0} / {limit ? limit.limit_value : 0}</span>
                        </div>
                        <ProgressBar 
                          variant={getLimitColor(featureName)}
                          now={percentage}
                          label={`${percentage}%`}
                        />
                      </div>
                      
                      <div className="row text-center">
                        <div className="col-6">
                          <div className="border-end">
                            <div className="fs-4 text-primary">{remaining}</div>
                            <small className="text-muted">Remaining</small>
                          </div>
                        </div>
                        <div className="col-6">
                          <div>
                            <div className="fs-4 text-info">{limit ? limit.limit_period : 'N/A'}</div>
                            <small className="text-muted">Period</small>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </div>
          );
        })}
      </div>
      </div>
    </SideNav>
  );
};

export default FeatureLimitsDashboard;
